// v3.18-1348 LAC-FIX — ClientLiveActivityStrip (high-visibility rewrite).
//
// The previous release rendered an amber "Live" pill that operators
// missed because it looked like every other banner. The redesigned
// strip renders a HIGH-VISIBILITY red banner for every ACTIVE
// workflow claim currently held on this client — by OTHER users.
// The current user's own claim is filtered out so the strip only
// surfaces potential collisions and situational awareness ("Jessica
// is submitting financing offers right now").
//
// Semantics per user directive (v3.18-1348):
//   • MULTIPLE workflow claims are ALL shown — never collapsed into
//     "Also viewing". Each claim gets its own row.
//   • Each row shows: activity label · user name · started_at ·
//     last_heartbeat (freshness indicator).
//   • Colour ring reflects freshness: red = fresh (<40s since
//     heartbeat), amber = warming (40-60s), grey = stale (about to
//     expire).
//   • Consumer callers use `onCount(n)` to know how many workflow
//     claims are visible so they can hide the generic "Also viewing"
//     row when workflow claims take precedence.
//
// Polls /live-activity-claims/for-client/{clientId} every 15 seconds
// (below the 20s heartbeat cadence + 60s stale cutoff so we always
// see fresh state).
import { useEffect, useState, useCallback } from 'react';
import { Radio, Clock } from 'lucide-react';
import api from '../lib/api';

const POLL_MS = 15_000;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
};

const secondsAgo = (iso) => {
  if (!iso) return null;
  try {
    return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  } catch { return null; }
};

const freshnessTone = (lastBeat) => {
  const s = secondsAgo(lastBeat);
  if (s == null) return 'red';
  if (s <= 40) return 'red';
  if (s <= 60) return 'amber';
  return 'slate';
};

const WORKFLOW_ICON_HINT = {
  entering_credit_application:  '📝',
  submitting_financing_offers:  '💸',
  creating_financing_stack:     '🧩',
  editing_deal_ownership:       '✏️',
  editing_client_information:   '🪪',
  creating_contract:            '📄',
  editing_contract:             '📄',
  collecting_payment:           '💳',
  scheduling_appointment:       '📅',
  editing_appointment:          '📅',
  sending_email:                '✉️',
  sending_sms:                  '💬',
  creating_task:                '✅',
  completing_follow_up:         '☑️',
  call_outcome:                 '📞',
  document_collection:          '📎',
  funding_review:               '🏦',
  funding_submission:           '🏦',
  collections:                  '⚠️',
  ghost_workflow:               '👻',
};

export function ClientLiveActivityStrip({ clientId, currentUserId, onCount }) {
  const [claims, setClaims] = useState([]);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/live-activity-claims/for-client/${clientId}`);
      const others = (data?.claims || []).filter(c => c.user_id !== currentUserId);
      setClaims(others);
      if (onCount) onCount(others.length);
    } catch { /* silent */ }
  }, [clientId, currentUserId, onCount]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (claims.length === 0) return null;

  return (
    <div
      className="mb-3 space-y-1.5"
      data-testid="client-live-activity-strip"
    >
      {claims.map(c => {
        const tone = freshnessTone(c.last_heartbeat);
        const containerCls =
          tone === 'red'
            ? 'border-red-400 bg-red-50 text-red-900 ring-2 ring-red-200'
            : tone === 'amber'
            ? 'border-amber-400 bg-amber-50 text-amber-900 ring-2 ring-amber-200'
            : 'border-slate-300 bg-slate-50 text-slate-800';
        const dotCls =
          tone === 'red' ? 'text-red-600 animate-pulse' :
          tone === 'amber' ? 'text-amber-600 animate-pulse' :
          'text-slate-400';
        const label = (c.activity_label || c.workflow || '').toLowerCase();
        const icon  = WORKFLOW_ICON_HINT[c.workflow] || '🔴';
        return (
          <div
            key={c.id}
            className={`px-3.5 py-2.5 rounded-lg border-2 flex items-center gap-3 text-[13px] shadow-sm ${containerCls}`}
            data-testid={`live-activity-strip-${c.workflow}`}
          >
            <div className="flex-shrink-0 flex items-center gap-1.5">
              <Radio size={16} className={dotCls} />
              <span className="text-[10px] uppercase tracking-widest font-extrabold">Live Activity</span>
            </div>
            <div className="min-w-0 flex-1 leading-snug">
              <span className="mr-1.5" aria-hidden="true">{icon}</span>
              <b>{c.user_name || c.user_email || 'Someone'}</b>
              <span> is currently </span>
              <b className="capitalize">{label}</b>
              <span>.</span>
            </div>
            <div className="text-[11px] flex-shrink-0 flex items-center gap-2 opacity-90">
              <span className="flex items-center gap-1">
                <Clock size={11} />
                Started {fmtTime(c.started_at)}
              </span>
              <span>·</span>
              <span data-testid={`live-activity-strip-heartbeat-${c.workflow}`}>
                Last heartbeat {fmtTime(c.last_heartbeat)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ClientLiveActivityStrip;
